import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgLaborBay, MfgLaborRateItem } from "@/lib/manufacturing/laborBillingClient";
import type { LaborRateItemDraft } from "./useManufacturingLaborBilling";

function money(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function LaborRateItemForm({
  title,
  draft,
  setDraft,
  busy,
  onCancel,
  onSubmit,
}: {
  title: string;
  draft: LaborRateItemDraft;
  setDraft: (next: LaborRateItemDraft) => void;
  busy: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-900/40">
      <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-zinc-100">{title}</h4>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Item code
          <input
            className={inputClass}
            value={draft.item_code}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, item_code: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Label
          <input
            className={inputClass}
            value={draft.label}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Flat rate (USD)
          <input
            className={inputClass}
            type="number"
            min={0}
            step="0.01"
            value={draft.flat_rate_usd}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, flat_rate_usd: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Option code (optional)
          <input
            className={inputClass}
            value={draft.option_code}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, option_code: e.target.value })}
          />
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-zinc-300 sm:col-span-2">
          <input
            type="checkbox"
            checked={draft.roof_eligible}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, roof_eligible: e.target.checked })}
          />
          Roof eligible (roofing bay can earn this line)
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400 sm:col-span-2">
          Notes
          <input
            className={inputClass}
            value={draft.notes}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={mfgPrimaryBtnClass} disabled={busy} onClick={onSubmit}>
          Save
        </button>
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function LaborRateItemsList({
  items,
  bays,
  linksByItem,
  busy,
  editingItemId,
  onEdit,
  onToggleActive,
  onRemove,
  onSetBayLinks,
}: {
  items: MfgLaborRateItem[];
  bays: MfgLaborBay[];
  linksByItem: Record<string, string[]>;
  busy: boolean;
  editingItemId: string | null;
  onEdit: (item: MfgLaborRateItem) => void;
  onToggleActive: (item: MfgLaborRateItem) => void;
  onRemove: (itemId: string) => void;
  onSetBayLinks: (rateItemId: string, bayIds: string[]) => void;
}) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        No rate items yet. Add free-text labor codes with a flat piece rate.
      </p>
    );
  }

  const activeBays = bays.filter((b) => b.is_active);

  return (
    <ul className="divide-y divide-gray-200 dark:divide-zinc-700">
      {items.map((item) => {
        const linked = new Set(linksByItem[item.id] ?? []);
        return (
          <li
            key={item.id}
            className={`space-y-2 py-3 ${
              editingItemId === item.id ? "bg-amber-50/60 dark:bg-amber-950/20" : ""
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-zinc-100">{item.label}</span>
                  <code className="text-xs text-gray-500 dark:text-zinc-400">{item.item_code}</code>
                  <span className="text-sm font-semibold text-gray-800 dark:text-zinc-200">
                    {money(item.flat_rate_usd)}
                  </span>
                  {!item.is_active ? (
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                      Inactive
                    </span>
                  ) : null}
                  {!item.roof_eligible ? (
                    <span className="text-[11px] text-gray-500 dark:text-zinc-400">
                      Not roof-eligible
                    </span>
                  ) : null}
                </div>
                {item.option_code ? (
                  <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                    Option code: {item.option_code}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={busy}
                  onClick={() => onEdit(item)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={busy}
                  onClick={() => onToggleActive(item)}
                >
                  {item.is_active ? "Deactivate" : "Activate"}
                </button>
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={busy}
                  onClick={() => onRemove(item.id)}
                >
                  Remove
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 pl-0.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                Earns in
              </span>
              {activeBays.length === 0 ? (
                <span className="text-xs text-gray-500 dark:text-zinc-400">No active bays</span>
              ) : (
                activeBays.map((bay) => {
                  const checked = linked.has(bay.id);
                  return (
                    <label
                      key={bay.id}
                      className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-zinc-300"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={busy}
                        onChange={() => {
                          const next = new Set(linked);
                          if (checked) next.delete(bay.id);
                          else next.add(bay.id);
                          onSetBayLinks(item.id, [...next]);
                        }}
                      />
                      {bay.name}
                    </label>
                  );
                })
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
